import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Calendar, Target, Lightbulb, CheckCircle2, Users, Phone } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import CallbackForm from "@/components/site/CallbackForm";
import { useSeo } from "@/lib/useSeo";
import { COMPANY } from "@/data/company";

const isVideo = (u = "") => /\.(mp4|webm|mov)(\?|$)/i.test(u);

export default function RealisationDetail() {
  const { slug } = useParams();
  const [r, setR] = useState(null);
  const [active, setActive] = useState(0);
  useEffect(() => {
    api.get(`/realisations/${slug}`).then((res) => { setR(res.data); setActive(0); }).catch(() => setR(false));
  }, [slug]);
  useSeo(r ? r.title : "Réalisation", r ? r.summary : "");

  if (r === false) return <div className="pt-40 pb-20 text-center text-ink/60">Réalisation introuvable. <Link to="/realisations" className="text-primary">Retour</Link></div>;
  if (!r) return <div className="pt-40 pb-20 text-center text-ink/50">Chargement…</div>;

  const media = [r.cover_image, ...(r.gallery || [])].filter(Boolean);
  const current = media[active] || media[0];

  const blocks = [
    { icon: Target, title: "Le brief du projet", text: r.challenge },
    { icon: Users, title: "Les moyens mis en place", text: r.solution },
    { icon: CheckCircle2, title: "Le résultat", text: r.result },
  ].filter((b) => b.text);

  return (
    <div>
      {/* Breadcrumb / header */}
      <div className="pt-28 pb-6 border-b border-border bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/realisations" className="inline-flex items-center gap-2 text-ink/50 hover:text-primary text-sm"><ArrowLeft size={16} /> Toutes les réalisations</Link>
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-primary font-bold text-sm">{r.category}</span>
            {r.location && <span className="inline-flex items-center gap-1.5 text-ink/50 text-sm"><MapPin size={14} /> {r.location}</span>}
            {r.year && <span className="inline-flex items-center gap-1.5 text-ink/50 text-sm"><Calendar size={14} /> {r.year}</span>}
          </div>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl lg:text-5xl font-black text-ink max-w-4xl">{r.title}</h1>
        </div>
      </div>

      {/* Two-column: gallery left, content right */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14 grid grid-cols-1 lg:grid-cols-2 gap-10 xl:gap-14">
        {/* LEFT — media gallery */}
        <div className="lg:sticky lg:top-24 self-start">
          <div className="overflow-hidden rounded-2xl bg-muted aspect-[4/3] border border-border">
            {current ? (
              isVideo(current)
                ? <video src={fileUrl(current)} controls autoPlay muted loop playsInline className="w-full h-full object-cover" />
                : <img src={fileUrl(current)} alt={r.title} className="w-full h-full object-cover" />
            ) : <div className="w-full h-full" />}
          </div>
          {media.length > 1 && (
            <div className="mt-3 grid grid-cols-4 gap-3">
              {media.map((m, i) => (
                <button key={i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`}
                  className={`relative overflow-hidden rounded-lg aspect-square border-2 transition ${active === i ? "border-primary" : "border-transparent opacity-70 hover:opacity-100"}`}>
                  {isVideo(m)
                    ? <video src={fileUrl(m)} muted className="w-full h-full object-cover" />
                    : <img src={fileUrl(m)} alt="" className="w-full h-full object-cover" />}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT — brief / means / result */}
        <div>
          {r.summary && (
            <div className="bg-ink text-white rounded-2xl p-6 sm:p-7 mb-6">
              <p className="text-lg leading-relaxed">{r.summary}</p>
            </div>
          )}
          <div className="space-y-4">
            {blocks.map((b) => (
              <div key={b.title} className="bg-card border border-border rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-10 w-10 rounded-lg bg-accent text-primary flex items-center justify-center shrink-0"><b.icon size={20} /></div>
                  <h2 className="font-heading text-lg font-black text-ink">{b.title}</h2>
                </div>
                <p className="text-ink/70 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-primary text-white px-6 py-3.5 rounded-full font-bold hover:brightness-95 transition">Un projet similaire ? Devis gratuit</Link>
            <a href={`tel:${COMPANY.phoneHref}`} className="inline-flex items-center justify-center gap-2 bg-ink text-white px-6 py-3.5 rounded-full font-bold hover:bg-primary transition"><Phone size={18} /> {COMPANY.phone}</a>
          </div>
        </div>
      </section>

      {/* CTA form */}
      <section className="bg-secondary/40 py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-2"><Lightbulb size={22} className="text-primary" /><h3 className="font-heading text-2xl sm:text-3xl font-black text-ink">Parlons de votre projet</h3></div>
          <p className="text-ink/60 mb-6">Recevez une étude et un devis gratuits, sans engagement.</p>
          <div className="bg-card border border-border rounded-2xl p-6 sm:p-8"><CallbackForm source={`realisation:${slug}`} /></div>
        </div>
      </section>
    </div>
  );
}
